import { useEffect, useLayoutEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { EmailLink } from "../components/EmailLink";
import { COMPANY } from "../lib/company";
import { findPartner, type Partner } from "../partner/partners";
import { ContactForm } from "./ContactForm";
import { contactStructuredData } from "./structuredData";
import type { ContactState } from "./types";
import "./contact.css";

/**
 * A layout effect on the client, a plain effect during the prerender.
 *
 * `useLayoutEffect` runs in the same commit as hydration, before the browser
 * paints again; `useEffect` runs after a paint. With partner mode that paint is
 * the whole non-partner page — hero, headline, three aside cards and the
 * briefing grid — flashing up and collapsing, which is far more obvious than
 * the single card that used to swap. React warns about `useLayoutEffect` when
 * there is no DOM, so the prerender pass gets the other one; it has no layout
 * to flash and its output is the no-partner page either way.
 *
 * The static HTML still paints before the bundle loads. That is prerendering
 * working as designed and cannot be effect-timed away — this removes the second
 * flash, not the first.
 */
const useAfterHydration =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * `/contact` — the one public place an enquiry starts.
 *
 * THIS PAGE USED TO REFUSE TO RENDER A FORM, and the reason it gave was exact:
 * "there is no endpoint behind one, and a control that looks like it submits
 * but only clears itself is worse than no form at all." That objection is now
 * answered rather than overruled — app/worker/siminosocial-contact-us is the
 * endpoint, contact.messages is where it lands, and the admin inbox is where it
 * is read. The mailbox and the phone number stay exactly where they were, as
 * the route for anyone who would rather not use a form at all.
 *
 * It keeps `?partner=`, so a partner page's call to action lands here with its
 * context intact instead of dropping the reader on a blank page that has
 * forgotten why they clicked. The query value is never rendered: it is looked
 * up in a table and anything unrecognised is ignored, so the page cannot be
 * made to display text an attacker put in a link.
 *
 * `?state=` is the return path from the confirmation email:
 *
 *   confirmed   they clicked the link and the message is with us
 *   invalid     expired, already used, or wrong — one outcome on purpose,
 *               because distinguishing them tells anybody holding a guessed
 *               token which guesses are close
 *   error       the confirm reached us and the database did not answer
 *
 * The route is public and must stay public: whoever clicks that link has no
 * account, and sending them through a sign-in gate to read "your message is
 * confirmed" would be absurd.
 */
export function ContactPage() {
  const { search } = useLocation();

  /**
   * Both resolved after mount, never during render.
   *
   * This page is pre-rendered to static HTML at a path with no query string,
   * and `main.tsx` hydrates that HTML. Reading `search` during the first render
   * would make the client's tree disagree with the server's and break
   * hydration, so the page renders its general state first and adopts the
   * partner and the confirmation outcome on the next tick.
   */
  const [partner, setPartner] = useState<Partner | null>(null);
  const [state, setState] = useState<ContactState | null>(null);
  useAfterHydration(() => {
    const params = new URLSearchParams(search);
    setPartner(findPartner(params.get("partner")));
    const raw = params.get("state");
    setState(raw === "confirmed" || raw === "invalid" || raw === "error" ? raw : null);
  }, [search]);

  const subject = partner ? partner.subject : `${COMPANY.product} enquiry`;
  const mailHref = `mailto:${COMPANY.supportEmail}?subject=${encodeURIComponent(subject)}`;
  const phoneHref = `tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(contactStructuredData()).replace(/</g, "\\u003c"),
        }}
      />

      {/*
        NO HERO IN PARTNER MODE. Somebody arriving from a partner's call to
        action has already read the pitch on the page they came from; repeating
        "Tell us what you need made" above the form makes them scroll past copy
        they have just agreed with to reach the thing they clicked for. The
        page becomes two columns and nothing else: the message on the left, who
        it is about on the right.
      */}
      {!partner && (
      <section className="hero">
        <div className="hero-inner contact-hero-inner">
          <div className="hero-copy">
            <p className="hero-badge"><span aria-hidden="true">✦</span> Contact</p>
            <h1>
              <span>Tell us what you</span>
              <span className="hero-gradient-text">need made.</span>
            </h1>
            {/* Unconditional: this hero only renders when there is no partner. */}
            <p className="lede">
              Questions about {COMPANY.product}, our creative partners, or a piece of
              content you need produced. A person reads every message.
            </p>
            <div className="hero-cta">
              <a className="btn-link primary lg" href="#contact-form">
                Write to us <span aria-hidden="true">→</span>
              </a>
              <a className="btn-link lg" href={phoneHref}>Call {COMPANY.phone}</a>
            </div>
            <p className="contact-note">
              Prefer to look around first? See{" "}
              <Link to="/partners/content-creator">why brands still need a human creator</Link>
              {" "}or our <Link to="/support">support page</Link> for help with the app itself.
            </p>
          </div>

          <div className="hero-visual">
            <section className="partner-panel partner-panel-hero" aria-labelledby="contact-reach-title">
              <p className="eyebrow">How to reach us</p>
              <h2 id="contact-reach-title">One mailbox, monitored.</h2>
              <dl className="partner-facts partner-facts-contact">
                <div>
                  <dt>Email</dt>
                  <dd><EmailLink address={COMPANY.supportEmail} /></dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd><a href={phoneHref}>{COMPANY.phone}</a></dd>
                </div>
                <div>
                  <dt>Company</dt>
                  <dd>{COMPANY.legalName}</dd>
                </div>
                <div>
                  <dt>Address</dt>
                  <dd>{COMPANY.address.oneLine}</dd>
                </div>
              </dl>
              <p className="partner-panel-note">
                App not behaving? <Link to="/support">Support</Link> gets there faster.
              </p>
            </section>
          </div>
        </div>
      </section>
      )}

      {/*
        The whole of partner mode is this one band: the form on the left, the
        partner card on the right, nothing else on the page. The three cards
        that sit beside the form otherwise — a second mailbox, the Grievance
        Officer, the registered company details — answer questions a partner
        enquiry did not ask, and push the one thing they came for below the
        fold. They are all still one click away on /support and /terms.
      */}
      <section className={partner ? "band contact-main contact-main-partner" : "band contact-main"} id="contact-form">
        <div className={partner ? "band-inner contact-layout contact-layout-partner" : "band-inner contact-layout"}>
          <div className="contact-primary">
            {state === "confirmed" && (
              <div className="card contact-confirmed" role="status">
                <p className="contact-sent-title">Confirmed — it is with us.</p>
                <p>
                  Your message is now in front of a person, and there is an acknowledgement in
                  your inbox with the reference number. Quote it if you write again about the
                  same thing.
                </p>
              </div>
            )}

            {state === "invalid" && (
              <div className="card err contact-problem" role="alert">
                <p>
                  That link is no longer valid — it may have expired, or already been used.
                  Nothing is lost: send the message again below and we will email a fresh link.
                </p>
              </div>
            )}

            {state === "error" && (
              <div className="card err contact-problem" role="alert">
                <p>
                  Something went wrong on our side while confirming, and your message is
                  safe. Try the link once more, and if it still fails, email{" "}
                  <EmailLink address={COMPANY.supportEmail} /> directly.
                </p>
              </div>
            )}

            {state !== "confirmed" && (
              <>
                {/* The page has no hero in partner mode, so this is its only
                    heading and has to be the h1. */}
                {partner
                  ? <h1 className="section-title">Send us a message</h1>
                  : <h2 className="section-title">Send us a message</h2>}
                <p className="lede">
                  You do not need an account. We email you a link to confirm the address, and
                  that click is the only step between you and a reply.
                </p>
                <ContactForm
                  partner={partner}
                  source={partner ? `partner-${partner.key}` : "contact-page"}
                />
              </>
            )}
          </div>

          {partner ? (
            <aside className="contact-aside" aria-label={`About ${partner.name}`}>
              {/*
                The same card as the one the hero shows otherwise, minus its
                "Start a … enquiry" button: that button scrolls to the form,
                and here the form is the next thing along.
              */}
              <section className={`partner-panel ${partner.plate}`} aria-labelledby="contact-partner-title">
                <p className="gz-logo-plate">
                  <img src={partner.logo} alt={`${partner.name} logo`} width="293" height="50" />
                </p>
                <p className="eyebrow">Enquiring about</p>
                <h2 id="contact-partner-title">{partner.name}</h2>
                <p className="contact-partner-blurb">{partner.blurb}</p>
                <p className="contact-bundle-flag">
                  <span aria-hidden="true">✦</span>
                  <span>
                    <strong>Partner bundle applies.</strong> Because you came through
                    {" "}{COMPANY.product}, {partner.bundle} &mdash; confirmed in writing
                    before anything is committed.
                  </span>
                </p>
                <p className="partner-panel-note">
                  <Link to={partner.profile}>Read the {partner.name} profile first &rarr;</Link>
                </p>
              </section>
            </aside>
          ) : (
            <aside className="contact-aside" aria-label="Other ways to reach us">
            <div className="card contact-card">
              <h2>Would rather not use a form?</h2>
              <p className="muted small">
                Write to the same mailbox directly, or call. Both reach the same people — the
                form exists because it collects what we would otherwise have to ask for.
              </p>
              <dl className="contact-details">
                <dt>Email</dt>
                <dd>
                  <EmailLink address={COMPANY.supportEmail} href={mailHref} label="Email us" />
                </dd>
                <dt>Phone</dt>
                <dd><a href={phoneHref}>{COMPANY.phone}</a></dd>
              </dl>
            </div>

            {/*
              Rule 3(2)(a) of the IT (Intermediary Guidelines and Digital Media Ethics Code)
              Rules, 2021 requires the officer's NAME and contact to be published — not just
              the role and a mailbox. The same details appear on the Terms page and in the
              mobile app, and the three are cross-checked.
            */}
            <div className="card contact-card">
              <h2>Grievance Officer</h2>
              <p className="muted small">
                For complaints under the Indian intermediary rules. Choose “Complaint” in the
                form and it is labelled for this queue.
              </p>
              <dl className="contact-details">
                <dt>Name</dt>
                <dd>{COMPANY.grievance.officerName}</dd>
                <dt>Email</dt>
                <dd><EmailLink address={COMPANY.grievance.email} /></dd>
                <dt>Response</dt>
                <dd>
                  Acknowledged within {COMPANY.grievance.acknowledgeHours} hours, resolved
                  within {COMPANY.grievance.resolveDays} days.
                </dd>
              </dl>
            </div>

            <div className="card contact-card">
              <h2>Who you are writing to</h2>
              <dl className="contact-details">
                <dt>Company</dt>
                <dd>{COMPANY.legalName}</dd>
                <dt>Office</dt>
                <dd>{COMPANY.address.oneLine}</dd>
                <dt>GSTIN</dt>
                <dd>{COMPANY.gstin}</dd>
              </dl>
              <p className="muted small">
                For account deletion see <Link to="/data-deletion">deleting your data</Link>.
              </p>
            </div>
            </aside>
          )}
        </div>
      </section>

      {/* Generic briefing advice. In partner mode the partner card beside the
          form already says what the enquiry is for, and this band is the last
          thing between the reader and a simple page. */}
      {!partner && (
      <section className="band alt features-band">
        <div className="band-inner">
          <h2 className="section-title">What to put in your first message</h2>
          <p className="lede">None of it is mandatory. Each one removes a round trip.</p>
          <div className="grid">
            {INCLUDE.map(item => (
              <div className="feature" key={item.title}>
                <span className="feature-icon" aria-hidden="true">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}
    </>
  );
}

/** What to put in an enquiry, so the first reply can be useful rather than a form letter. */
const INCLUDE = [
  {
    icon: "◇",
    title: "What you are trying to explain",
    body: "The product, the audience, and the thing people keep misunderstanding about it. This matters more than the format.",
  },
  {
    icon: "▣",
    title: "Where it will be used",
    body: "A landing page, a pitch, an onboarding flow, paid social, or your Facebook, Instagram and YouTube channels.",
  },
  {
    icon: "◷",
    title: "When you need it",
    body: "A real date, even an approximate one. It changes what is worth proposing more than any other single detail.",
  },
  {
    icon: "◎",
    title: "Anything you already have",
    body: "Brand guidelines, a script, an old deck, or a piece you liked from someone else. Existing material shortens everything.",
  },
];
